import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInDays, format, parseISO } from 'date-fns';
import { useDateLocale } from '../../../utils/dateLocale';
import { Icon, Pencil, Trash2, Bell, ExternalLink, Paperclip, ChevronDown, Train, Ticket, Sparkles, Package, type LucideIcon } from '../../ui/Icon';
import { fileIcon } from './documentFiles';
import { TicketExpenseActions, TicketExpenseBadge } from './TicketExpenses';
import { bookingOption } from '../../../utils/reservations';
import type { Booking, BookingDocument } from '../../../types';

const R2_PUBLIC_URL = (import.meta.env.VITE_R2_PUBLIC_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const fileUrl = (filename: string) => `${R2_PUBLIC_URL}/${filename}`;

const TYPE_COLORS: Record<string, string> = {
  TRANSPORT: 'bg-sky-50 text-sky-600 dark:bg-sky-900/20 dark:text-sky-400',
  EVENT:     'bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400',
  ACTIVITY:  'bg-teal-50 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400',
  OTHER:     'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

// Stripe tint per booking type — soft tint for the icon column, paired with TYPE_COLORS pill
const STRIPE_TINTS: Record<string, string> = {
  TRANSPORT: 'bg-sky-50/70 dark:bg-sky-900/15',
  EVENT:     'bg-violet-50/70 dark:bg-violet-900/15',
  ACTIVITY:  'bg-teal-50/70 dark:bg-teal-900/15',
  OTHER:     'bg-stone-50 dark:bg-slate-800/60',
};

const STRIPE_ICON_COLORS: Record<string, string> = {
  TRANSPORT: 'text-sky-500 dark:text-sky-400',
  EVENT:     'text-violet-500 dark:text-violet-400',
  ACTIVITY:  'text-teal-500 dark:text-teal-400',
  OTHER:     'text-slate-400 dark:text-slate-500',
};

const STRIPE_ICONS: Record<string, LucideIcon> = {
  TRANSPORT: Train,
  EVENT:     Ticket,
  ACTIVITY:  Sparkles,
  OTHER:     Package,
};

interface Props {
  booking: Booking;
  docs?: BookingDocument[];
  onEdit: () => void;
  onDelete: () => void;
}

export function ExperienceTicket({ booking, docs = [], onEdit, onDelete }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const reservation = bookingOption(booking);
  const [open, setOpen] = useState(false);
  const typeColor   = TYPE_COLORS[booking.type] ?? TYPE_COLORS.OTHER;
  const stripeTint  = STRIPE_TINTS[booking.type] ?? STRIPE_TINTS.OTHER;
  const stripeColor = STRIPE_ICON_COLORS[booking.type] ?? STRIPE_ICON_COLORS.OTHER;
  const StripeIcon  = STRIPE_ICONS[booking.type] ?? STRIPE_ICONS.OTHER;

  // Both are YYYY-MM-DD here, so a string compare is a chronological one. A
  // same-day endDate is not a range worth drawing twice.
  const startDay = booking.date?.slice(0, 10) ?? null;
  const endDay   = booking.endDate?.slice(0, 10) ?? null;
  const spansDays = !!startDay && !!endDay && endDay > startDay;
  const dayCount = spansDays ? differenceInDays(parseISO(endDay), parseISO(startDay)) : 0;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-card hover:shadow-hero hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-150">

      {/* ── Mobile header ── */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="md:hidden w-full text-left flex items-stretch cursor-pointer"
      >
        {/* Icon / type strip */}
        <div className={`${stripeTint} flex flex-col items-center justify-center px-2 py-3 gap-1.5 shrink-0 w-12`}>
          <Icon as={StripeIcon} size={18} className={stripeColor} />
          <span className="font-mono text-[9px] tracking-widest uppercase text-slate-500 dark:text-slate-400 font-semibold"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            {booking.type}
          </span>
        </div>

        {/* Main content */}
        <div className="flex-1 px-4 py-3.5 min-w-0">
          <p className="font-semibold text-base text-slate-900 dark:text-slate-100 truncate leading-tight">{booking.title}</p>

          {/* Date + time row */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {startDay && (
              <span className="font-serif italic font-bold text-lg text-slate-800 dark:text-slate-200">
                {format(parseISO(startDay), 'dd MMM yyyy', { locale: dateLocale })}
              </span>
            )}
            {booking.startTime && (
              <span className="font-mono text-[12px] text-teal-600 dark:text-teal-400 font-semibold tabular-nums">{booking.startTime}</span>
            )}
            {spansDays ? (
              <>
                <span className="text-slate-300 dark:text-slate-600">→</span>
                <span className="font-serif italic font-bold text-lg text-slate-800 dark:text-slate-200">
                  {format(parseISO(endDay), 'dd MMM yyyy', { locale: dateLocale })}
                </span>
                {booking.endTime && (
                  <span className="font-mono text-[12px] text-teal-600 dark:text-teal-400 font-semibold tabular-nums">{booking.endTime}</span>
                )}
                <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-semibold">
                  {t('bookings.durationDays', { count: dayCount })}
                </span>
              </>
            ) : booking.endTime && (
              <span className="font-mono text-[12px] text-slate-400 dark:text-slate-500 tabular-nums">– {booking.endTime}</span>
            )}
          </div>

          {/* Meta row */}
          <div className="flex items-center gap-2.5 mt-2 flex-wrap">
            {booking.venue && (
              <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-[180px]">{booking.venue}</span>
            )}
            {booking.reminderMinutes && (
              <span className="flex items-center gap-1 font-mono text-[11px] text-amber-600 dark:text-amber-400 font-semibold">
                <Icon as={Bell} size={10} />
                {booking.reminderMinutes >= 60 ? `${booking.reminderMinutes / 60}h` : `${booking.reminderMinutes}min`}
              </span>
            )}
            {docs.length > 0 && (
              <span className="flex items-center gap-1 font-mono text-[11px] text-slate-500 dark:text-slate-400">
                <Icon as={Paperclip} size={10} /> {docs.length}
              </span>
            )}
            <TicketExpenseBadge link={reservation.link} />
          </div>
        </div>

        {/* Chevron */}
        <div className="flex items-center px-3">
          <Icon
            as={ChevronDown}
            size={16}
            className={`text-slate-300 dark:text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* ── Desktop header ── */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="hidden md:grid w-full text-left grid-cols-[56px_120px_1.4fr_1fr] divide-x divide-dashed divide-slate-200 dark:divide-slate-700 cursor-pointer"
      >
        {/* Col 1: Type stripe */}
        <div className={`${stripeTint} flex flex-col items-center justify-center px-2 py-4 gap-2`}>
          <Icon as={StripeIcon} size={20} className={stripeColor} />
          <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500 dark:text-slate-400 font-semibold"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            {booking.type}
          </span>
        </div>

        {/* Col 2: Date / time — a booking that spans days shows both ends */}
        <div className="flex flex-col items-center justify-center px-3 py-5 gap-1 w-full min-w-0">
          {startDay && (
            <>
              <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400 font-semibold">
                {format(parseISO(startDay), 'MMM', { locale: dateLocale })}
              </span>
              <span className={`font-serif italic font-bold leading-none text-slate-900 dark:text-slate-100 tabular-nums ${spansDays ? 'text-[30px]' : 'text-[40px]'}`}>
                {format(parseISO(startDay), 'dd', { locale: dateLocale })}
              </span>
              <span className="font-mono text-[10px] text-slate-400 tabular-nums">
                {format(parseISO(startDay), 'EEE · yyyy', { locale: dateLocale })}
              </span>
            </>
          )}
          {booking.startTime && (
            <span className="font-mono text-[13px] text-teal-600 dark:text-teal-400 mt-1 font-semibold tabular-nums">{booking.startTime}</span>
          )}

          {spansDays && (
            <div className="w-full mt-2 pt-2 border-t border-dashed border-slate-200 dark:border-slate-700 flex flex-col items-center gap-0.5">
              <span className="font-mono text-[9px] tracking-widest uppercase text-slate-400 font-semibold">
                {format(parseISO(endDay), 'MMM', { locale: dateLocale })}
              </span>
              <span className="font-serif italic font-bold text-[30px] leading-none text-slate-900 dark:text-slate-100 tabular-nums">
                {format(parseISO(endDay), 'dd', { locale: dateLocale })}
              </span>
              <span className="font-mono text-[10px] text-slate-400 tabular-nums">
                {format(parseISO(endDay), 'EEE · yyyy', { locale: dateLocale })}
              </span>
              {booking.endTime && (
                <span className="font-mono text-[13px] text-teal-600 dark:text-teal-400 font-semibold tabular-nums">{booking.endTime}</span>
              )}
            </div>
          )}
        </div>

        {/* Col 3: Title + type pill */}
        <div className="flex flex-col justify-center px-5 py-5 gap-1.5 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`inline-flex items-center h-5 px-2 rounded-full text-[10px] font-semibold tracking-wider uppercase ${typeColor} w-fit`}>
              {booking.type}
            </span>
            {spansDays && (
              <span className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 w-fit">
                {t('bookings.durationDays', { count: dayCount })}
              </span>
            )}
          </div>
          <p className="font-semibold text-base text-slate-900 dark:text-slate-100 leading-snug line-clamp-2">{booking.title}</p>
          {booking.venue && (
            <span className="font-mono text-[12px] text-slate-500 dark:text-slate-400 truncate">{booking.venue}</span>
          )}
        </div>

        {/* Col 4: Meta + chevron */}
        <div className="flex flex-col justify-center px-5 py-5 gap-2 min-w-0">
          {booking.reminderMinutes && (
            <span className="flex items-center gap-1.5 font-mono text-[11px] text-amber-600 dark:text-amber-400 font-semibold">
              <Icon as={Bell} size={11} />
              {booking.reminderMinutes >= 60 ? `${booking.reminderMinutes / 60}h` : `${booking.reminderMinutes}min`}
            </span>
          )}
          {docs.length > 0 && (
            <span className="flex items-center gap-1.5 font-mono text-[11px] text-slate-500 dark:text-slate-400">
              <Icon as={Paperclip} size={11} /> {docs.length} {docs.length === 1 ? 'doc' : 'docs'}
            </span>
          )}
          <TicketExpenseBadge link={reservation.link} size={11} />
          {booking.notes && (
            <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500 line-clamp-2 whitespace-pre-wrap break-words">{booking.notes}</span>
          )}
          <Icon
            as={ChevronDown}
            size={16}
            className={`mt-auto text-slate-300 dark:text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* ── Expanded content ── */}
      {open && (
        <>
          {/* Documents */}
          {docs.length > 0 && (
            <div className="border-t border-slate-100 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-800/30 px-4 py-2 flex items-center gap-2 flex-wrap">
              <Icon as={Paperclip} size={11} className="text-slate-400 dark:text-slate-500 shrink-0" />
              {docs.map(doc => (
                <a
                  key={doc.id}
                  href={fileUrl(doc.filename)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-[10px] font-medium text-teal-600 dark:text-teal-400 hover:border-teal-300 dark:hover:border-teal-700 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors max-w-[180px]"
                  title={doc.originalName}
                >
                  <Icon as={fileIcon(doc.mimeType)} size={10} className="shrink-0" />
                  <span className="truncate">{doc.originalName}</span>
                </a>
              ))}
            </div>
          )}

          {/* Notes */}
          {booking.notes && (
            <div className="border-t border-dashed border-slate-200 dark:border-slate-700 bg-stone-50/60 dark:bg-slate-800/40 px-4 py-3">
              <p className="font-mono text-[11px] text-slate-500 dark:text-slate-400 whitespace-pre-wrap break-words">{booking.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-2 flex items-center justify-end gap-1">
            <TicketExpenseActions reservation={reservation} />
            <button
              onClick={e => { e.stopPropagation(); onEdit(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer"
            >
              <Icon as={Pencil} size={12} /> Editar
            </button>
            <button
              onClick={e => { e.stopPropagation(); onDelete(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 transition-colors cursor-pointer"
            >
              <Icon as={Trash2} size={12} /> Eliminar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
